import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import { modalBackdropCls, modalCardCls, modalCloseCls, wideOverlayCls } from './overlay';
import { useLanguage } from '../../contexts/LanguageContext';

// A target rectangle drawn on a reference image: normalized [x0,y0,x1,y1] (0..1).
export type Box = [number, number, number, number];

// One image and its zones, for the draw overlay's side-by-side panels. `drawable` false
// makes it a read-only reference (shown for comparison, but no zones can be drawn on it).
export type MarkPanel = {
  label: string;
  src: string;
  boxes: Box[];
  onBoxes: (b: Box[]) => void;
  drawable?: boolean;
  // SOMETHING OTHER THAN A PICTURE in the frame — a 3D viewer, in the one case there is.
  // Drawn instead of `src`, at `aside` size, and never drawable: a rectangle over a mesh
  // would be a rectangle over whatever happened to be turned towards you.
  node?: ReactNode;
  // Smaller than the panels being worked on. A view that is there to say WHERE you are is
  // not the thing being judged, and at the same size it would push what is off the screen.
  aside?: boolean;
  // THIS PANEL'S ANSWER IS BEING RECOMPUTED. Dimmed, with a spinner on it — the same two
  // marks ImageLightbox wears for the same fact, because it means the same thing: what is
  // on screen is the LAST answer, not the one to the question just asked. Without it a
  // correction that takes a second looks like a correction that did nothing, and the
  // difference between those is the whole reason to look.
  //
  // A SCRIM OVER IT, not opacity on it. Fading the picture makes the picture see-through
  // and what is behind it here is the panel, so a dimmed atlas would show slate through the
  // wood rather than reading as a picture waiting.
  waiting?: boolean;
  // SELECTIONS DRAWN OVER THE PICTURE, each an image that is white where it claims a texel,
  // and the hue to tint it. Shown before anything is applied, because a mask worked out from
  // the geometry is not something anybody can predict by looking at the mesh: the unwrap
  // decides where it lands, and seeing which scattered pieces of the atlas it caught IS the
  // check. Several, because several faces can be chosen at once and each one's colour is
  // how a piece of the square is tied back to a row of the list.
  overlays?: { url: string; rotate: number }[];
  // A CONTROL UNDER THIS PANEL'S OWN FRAME. Where a control acts on ONE of the pictures it
  // belongs beneath that picture and not in a row under all of them: "the face I am looking
  // at" is a sentence about the model, and under the middle of the panel it reads as being
  // about whichever picture happens to be above it.
  below?: ReactNode;
  // A CROSSHAIR AT [x, y] IN 0..1, or nothing. What the other panel is pointing at: the
  // atlas is a flayed square in which no part is recognisable, so this is how pointing at a
  // leg on the mesh says which region of the square that leg is.
  marker?: { x: number; y: number } | null;
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = src;
  });

// Paste the marked zones of `patch` back onto `base`, keeping `base` everywhere else — an
// inpainting composite. The zones (normalized to `base`) are cut from `patch` (scaled to
// base) through a feathered mask, so only the selected regions change and their edges blend
// into the untouched surroundings instead of leaving a hard rectangle seam. Returns a data
// URL. No pixels of the zones themselves are ever drawn, so nothing leaks into the result.
export async function compositeBoxes(baseSrc: string, patchSrc: string, boxes: Box[]): Promise<string> {
  const [base, patch] = await Promise.all([loadImage(baseSrc), loadImage(patchSrc)]);
  const w = base.naturalWidth || base.width;
  const h = base.naturalHeight || base.height;
  const make = () => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return [c, c.getContext('2d')] as const;
  };
  const [canvas, ctx] = make();
  const [pc, pctx] = make();
  const [mask, mctx] = make();
  if (!ctx || !pctx || !mctx) return patchSrc;
  ctx.drawImage(base, 0, 0, w, h);
  pctx.drawImage(patch, 0, 0, w, h);
  // A soft-edged white stencil of the zones — the blur feathers the boundary both ways.
  const feather = Math.max(4, Math.round(Math.min(w, h) * 0.02));
  mctx.filter = `blur(${feather}px)`;
  mctx.fillStyle = '#fff';
  for (const [x0, y0, x1, y1] of boxes) {
    mctx.fillRect(Math.min(x0, x1) * w, Math.min(y0, y1) * h, Math.abs(x1 - x0) * w, Math.abs(y1 - y0) * h);
  }
  // Keep the patch only where the stencil is opaque, then lay that over the untouched base.
  pctx.globalCompositeOperation = 'destination-in';
  pctx.drawImage(mask, 0, 0);
  ctx.drawImage(pc, 0, 0);
  return canvas.toDataURL('image/png');
}

export const boxStyle = (b: Box) => ({
  left: `${Math.min(b[0], b[2]) * 100}%`,
  top: `${Math.min(b[1], b[3]) * 100}%`,
  width: `${Math.abs(b[2] - b[0]) * 100}%`,
  height: `${Math.abs(b[3] - b[1]) * 100}%`,
});

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const inside = (b: Box, x: number, y: number) =>
  x >= Math.min(b[0], b[2]) &&
  x <= Math.max(b[0], b[2]) &&
  y >= Math.min(b[1], b[3]) &&
  y <= Math.max(b[1], b[3]);
// A copy of a box shifted by (dx,dy), kept fully inside the image (its size preserved).
const shiftBox = (b: Box, dx: number, dy: number): Box => {
  const w = b[2] - b[0];
  const h = b[3] - b[1];
  const x0 = Math.min(Math.max(b[0] + dx, 0), 1 - w);
  const y0 = Math.min(Math.max(b[1] + dy, 0), 1 - h);
  return [x0, y0, x0 + w, y0 + h];
};

type From = { panel: number; index: number };

// The overlay: every panel's image shown large, side by side; drawable ones take zones (a
// reference panel is shown only for comparison). Drag empty space to add a zone; drag a zone
// to move it — even ONTO another drawable image, which transfers it there; click one to
// select; ⌘/Ctrl+C then ⌘/Ctrl+V duplicates it; Delete removes it; "Effacer les zones"
// clears a panel. Backdrop / Escape closes.
// HOW BIG A PICTURE IS ALLOWED TO GET, and it is bounded by the SCREEN on both axes.
//
// It was `h-[54vh]` — a height alone, which is half an answer. These panels are roughly
// square, so a height is also a width, and three of them plus the face column had to fit
// across the window: on a laptop they did not, the row wrapped, and the column landed under
// the pictures. That is what pushed the frame past the bottom of the screen, and the row it
// left behind was no longer centred on anything.
//
// So each cap is a `min()` of three things: what the viewport's HEIGHT leaves once the
// captions, the buttons under the model and the footer are paid for (~230px); what its
// WIDTH leaves once the row is added up (two pictures, an aside at ~0.85 of one, the 14rem
// face column and the gaps); and a ceiling, because past ~540px a texture atlas is not
// telling you anything more.
//
// The width term is the one that stops the wrap, and it is why removing the sidebar's 15rem
// from the overlay padding matters twice over: it is 15rem of picture.
//
// ONE SIZE FOR ALL THREE, and a smaller one than the first pass at this. The atlases and
// the model beside them are a row that is read across, and three panels of three heights
// read as three unrelated things; the arithmetic is also easier to keep true when it is one
// sum — 3 panels + the 14rem face column + the gaps, against the window. 420px is enough to
// find a reflection on an atlas, and what is bought with the rest is the face column staying
// on the row where it belongs and the buttons under the footer staying on screen.
// (Written out three times rather than composed from one constant: Tailwind reads the
// source for whole class names, and a class it cannot see is a class it does not build.)
const PIC_H ='h-[min(calc(88dvh-300px),calc(33vw-119px),420px)]';
const ASIDE_H = 'h-[min(calc(88dvh-300px),calc(33vw-119px),420px)]';
const ASIDE_W = 'w-[min(calc(88dvh-300px),calc(33vw-119px),420px)]';
// One picture and nothing beside it — the regenerate's own zoom. Width is not the
// constraint there, so only the height's chrome is taken off.
const SINGLE_H = 'h-[min(80vh,calc(92dvh-150px))]';

// For a panel drawing its own thing rather than an image (the retouch's turnable model),
// which has to be sized by its caller: the same box the asides get here, and square.
export const MARK_ASIDE_SIZE = `${ASIDE_H} ${ASIDE_W}`;

export function MarkOverlay({ panels, footer, side, onClose }: {
  panels: MarkPanel[];
  // A COLUMN DOWN THE RIGHT-HAND EDGE, beside the pictures rather than under them. For a
  // list that outlives any one of them — the faces picked so far — where a row under the
  // panels would read as a caption on whichever picture it happened to sit below.
  side?: ReactNode;
  // WHAT TO SAY ABOUT THE ZONES, under them. Empty for the regenerate, which asks for
  // rectangles and nothing more; the texture retouch puts a strength beside each one,
  // because how hard to correct a bright patch is a different answer per patch.
  footer?: ReactNode;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const clip = useRef<Box | null>(null);
  const drag = useRef<{ kind: 'move' | 'draw' | 'resize'; origin: number; index: number; box: Box; grab: { x: number; y: number } } | null>(null);
  const [active, setActive] = useState(0);
  const [sel, setSel] = useState<From | null>(null);
  // The box being moved (shown floating, hidden at its origin) and the in-progress draw.
  const [floating, setFloating] = useState<{ panel: number; box: Box; from: From } | null>(null);
  const [draft, setDraft] = useState<{ panel: number; box: Box } | null>(null);

  const norm = (panel: number, cx: number, cy: number) => {
    const r = refs.current[panel]?.getBoundingClientRect();
    if (!r) return null;
    return { x: clamp01((cx - r.left) / r.width), y: clamp01((cy - r.top) / r.height) };
  };
  // Only drawable panels are hit for a drop, so a zone can never land on a reference image.
  const panelAt = (cx: number, cy: number) => {
    for (let i = 0; i < panels.length; i++) {
      if (panels[i].drawable === false) continue;
      const r = refs.current[i]?.getBoundingClientRect();
      if (r && cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) return i;
    }
    return -1;
  };
  const hitIndex = (panel: number, x: number, y: number) => {
    const bs = panels[panel].boxes;
    for (let i = bs.length - 1; i >= 0; i--) if (inside(bs[i], x, y)) return i;
    return -1;
  };
  // A CORNER, and the corner across from it — which is what a resize drags against: grab
  // the top-left and the bottom-right stays put, so the rectangle follows the hand from a
  // fixed anchor exactly as drawing one does.
  //
  // The reach is in PIXELS converted to the panel's own units, not a fraction of the box. A
  // fraction makes a small zone's corners unmissable and a large zone's corners unfindable,
  // and it is a distance on screen that a hand is actually aiming at.
  const GRAB_PX = 14;
  const cornerHit = (panel: number, x: number, y: number) => {
    const r = refs.current[panel]?.getBoundingClientRect();
    if (!r) return null;
    const [hx, hy] = [GRAB_PX / r.width, GRAB_PX / r.height];
    const bs = panels[panel].boxes;
    for (let i = bs.length - 1; i >= 0; i--) {
      const [ax, ay, bx, by] = bs[i];
      for (const [cx, cy, ox, oy] of [
        [ax, ay, bx, by], [bx, ay, ax, by], [ax, by, bx, ay], [bx, by, ax, ay],
      ]) {
        if (Math.abs(x - cx) <= hx && Math.abs(y - cy) <= hy) {
          return { index: i, anchor: { x: ox, y: oy } };
        }
      }
    }
    return null;
  };

  const onPanelDown = (panel: number) => (e: React.PointerEvent) => {
    if (panels[panel].drawable === false) return;
    e.preventDefault();
    setActive(panel);
    const p = norm(panel, e.clientX, e.clientY);
    if (!p) return;
    const corner = cornerHit(panel, p.x, p.y);
    if (corner) {
      setSel({ panel, index: corner.index });
      drag.current = {
        kind: 'resize', origin: panel, index: corner.index,
        box: panels[panel].boxes[corner.index], grab: corner.anchor,
      };
      setFloating({ panel, box: panels[panel].boxes[corner.index], from: { panel, index: corner.index } });
      return;
    }
    const hit = hitIndex(panel, p.x, p.y);
    if (hit >= 0) {
      const box = panels[panel].boxes[hit];
      setSel({ panel, index: hit });
      drag.current = { kind: 'move', origin: panel, index: hit, box, grab: { x: p.x - box[0], y: p.y - box[1] } };
      setFloating({ panel, box, from: { panel, index: hit } });
    } else {
      setSel(null);
      drag.current = { kind: 'draw', origin: panel, index: -1, box: [p.x, p.y, p.x, p.y], grab: p };
      setDraft({ panel, box: [p.x, p.y, p.x, p.y] });
    }
  };

  // The move / draw follows the pointer anywhere (window listeners), so a zone can be dragged
  // from one panel onto the other. Everything is committed on release.
  useEffect(() => {
    const dropBox = (d: NonNullable<typeof drag.current>, cx: number, cy: number) => {
      const tgt = panelAt(cx, cy);
      const panel = tgt >= 0 ? tgt : d.origin;
      const p = norm(panel, cx, cy);
      if (!p) return null;
      const w = d.box[2] - d.box[0];
      const h = d.box[3] - d.box[1];
      const x0 = Math.min(Math.max(p.x - d.grab.x, 0), 1 - w);
      const y0 = Math.min(Math.max(p.y - d.grab.y, 0), 1 - h);
      return { panel, box: [x0, y0, x0 + w, y0 + h] as Box };
    };
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      if (d.kind === 'draw') {
        const p = norm(d.origin, e.clientX, e.clientY);
        if (p) setDraft({ panel: d.origin, box: [d.grab.x, d.grab.y, p.x, p.y] });
      } else if (d.kind === 'resize') {
        // Shown floating so the zone underneath is hidden while it is being reshaped —
        // otherwise the old rectangle stays drawn under the new one the whole drag.
        const p = norm(d.origin, e.clientX, e.clientY);
        if (p) {
          setFloating({
            panel: d.origin, box: [d.grab.x, d.grab.y, p.x, p.y],
            from: { panel: d.origin, index: d.index },
          });
        }
      } else {
        const r = dropBox(d, e.clientX, e.clientY);
        if (r) setFloating({ panel: r.panel, box: r.box, from: { panel: d.origin, index: d.index } });
      }
    };
    const onUp = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      if (d.kind === 'resize') {
        setFloating(null);
        const p = norm(d.origin, e.clientX, e.clientY);
        // Dragged onto its own anchor is not a zone. The old rectangle stays rather than
        // becoming a sliver nobody can grab again.
        if (p && Math.abs(p.x - d.grab.x) > 0.02 && Math.abs(p.y - d.grab.y) > 0.02) {
          const nb: Box = [
            Math.min(d.grab.x, p.x), Math.min(d.grab.y, p.y),
            Math.max(d.grab.x, p.x), Math.max(d.grab.y, p.y),
          ];
          panels[d.origin].onBoxes(panels[d.origin].boxes.map((b, i) => (i === d.index ? nb : b)));
        }
        return;
      }
      if (d.kind === 'draw') {
        setDraft(null);
        const p = norm(d.origin, e.clientX, e.clientY);
        if (p && Math.abs(p.x - d.grab.x) > 0.02 && Math.abs(p.y - d.grab.y) > 0.02) {
          const nb: Box = [Math.min(d.grab.x, p.x), Math.min(d.grab.y, p.y), Math.max(d.grab.x, p.x), Math.max(d.grab.y, p.y)];
          panels[d.origin].onBoxes([...panels[d.origin].boxes, nb]);
          setSel({ panel: d.origin, index: panels[d.origin].boxes.length });
        }
        return;
      }
      setFloating(null);
      const r = dropBox(d, e.clientX, e.clientY);
      if (!r) return;
      if (r.panel === d.origin) {
        panels[d.origin].onBoxes(panels[d.origin].boxes.map((b, i) => (i === d.index ? r.box : b)));
        setSel({ panel: d.origin, index: d.index });
      } else {
        // Transfer to the other image.
        panels[d.origin].onBoxes(panels[d.origin].boxes.filter((_, i) => i !== d.index));
        panels[r.panel].onBoxes([...panels[r.panel].boxes, r.box]);
        setSel({ panel: r.panel, index: panels[r.panel].boxes.length });
        setActive(r.panel);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [panels]);

  // Copy / paste / delete the selected zone; Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // THE TOPMOST OVERLAY OWNS ESCAPE. This opens OVER something that listens for it
        // too — the regenerate dialog, the picture zoom — and both listen on `window`, so
        // one press dismissed this and whatever was underneath it in the same breath.
        // Caught on the way down and stopped here, so the press closes one thing.
        e.stopPropagation();
        return onClose();
      }
      if (!sel) return;
      const meta = e.metaKey || e.ctrlKey;
      const { panel, index } = sel;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        panels[panel].onBoxes(panels[panel].boxes.filter((_, i) => i !== index));
        setSel(null);
      } else if (meta && (e.key === 'c' || e.key === 'C')) {
        e.preventDefault();
        clip.current = panels[panel].boxes[index];
      } else if (meta && (e.key === 'v' || e.key === 'V') && clip.current) {
        e.preventDefault();
        panels[panel].onBoxes([...panels[panel].boxes, shiftBox(clip.current, 0.04, 0.04)]);
        setSel({ panel, index: panels[panel].boxes.length });
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [panels, sel, onClose]);

  const single = panels.filter((p) => !p.aside).length === 1;
  return (
    <div className={`${wideOverlayCls} ${modalBackdropCls}`} onClick={onClose}>
      {/* A COLUMN OF ROWS, not one wrapping row of everything.

          It was the latter, and that is why the frame's margins jumped about. The intrinsic
          width of a wrap container is every item laid out on ONE line — wrapping is not
          considered — so the pictures AND the footer were summed, the total blew past 95vw,
          the frame clamped there, and the pictures were then centred inside a box far wider
          than they were. Every sentence added to the footer widened that phantom line and
          moved the margins again, for a frame whose contents had not changed.

          A column's width is its WIDEST ROW. The pictures decide it, the footer sits under
          them at whatever width it needs, and the border is the padding and nothing else. */}
      <div
        // AND IT NEVER RUNS OFF THE SCREEN. The pictures above are sized so it should not
        // have to, but a footer is somebody's prose and grows in translation — and this is
        // centred in a fixed overlay, where anything taller than the window hangs off BOTH
        // ends with nowhere to scroll to. The frame keeps the overflow instead.
        //
        // THE CARD EVERY DIALOG WEARS (modalCardCls): white in light mode, slate-900 in dark,
        // with the close button in its corner. It was a slate-600 frame of its own, dark in
        // both themes, the one dialog that did not follow the app. The extra top padding is
        // the close button's row: at p-5 it sat on the first caption. Taken from the top and
        // not the sides, because the width is the sum the picture sizes above are cut to.
        className={`relative flex max-h-[96dvh] max-w-[95vw] flex-col items-center gap-5 overflow-y-auto px-5 pb-5 pt-10 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} aria-label="Close" className={modalCloseCls}>
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-wrap items-start justify-center gap-5">
        {panels.map((p, i) => {
          const canDraw = p.drawable !== false && !p.node;
          // Smaller where a panel shares the row with an aside: the pictures were taking
          // the whole width and leaving the view that says WHERE you are a quarter of it.
          const size = p.aside ? ASIDE_H : (single ? SINGLE_H : PIC_H);
          return (
          <figure
            key={i}
            // The aside is shorter than what it sits beside, and a row aligned to its top
            // left it hanging from the ceiling beside two full-height pictures. Centred
            // against them instead — it is a companion to both, not a header over either.
            className={`flex flex-col items-center gap-2 ${p.aside ? 'self-center' : ''}`}
          >
            <figcaption className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {p.label}
            </figcaption>
            <div
              ref={(el) => {
                refs.current[i] = el;
              }}
              onPointerDown={onPanelDown(i)}
              // An OUTLINE round the picture now the card is white too: a product shot on a
              // white ground had no edge left, and neither did the zone you drew up to it.
              // An outline and not a border, so the rectangle the zones are measured
              // against is still exactly the picture's.
              className={`relative select-none overflow-hidden rounded-xl bg-white shadow-md outline-1 outline-slate-200 ${
                canDraw ? 'cursor-crosshair' : 'cursor-default'
              } ${
                canDraw && i === active && !single ? 'ring-2 ring-blue-400' : ''}`}
            >
              {p.node ?? (
                <img src={p.src} alt="" draggable={false} className={`block w-auto ${size}`} />
              )}
              {/* WHERE THE OTHER PANEL IS POINTING. Lines to the edges rather than a dot:
                  a dot on a picture of wood is another speck among the thousands this
                  texture already has, where two rules crossing are found instantly and
                  read against the axes of the square. */}
              {p.overlays?.map((o) => (
                <img
                  key={o.url}
                  src={o.url}
                  alt=""
                  draggable={false}
                  // Screened over the wood in a colour rather than laid on it in white: the
                  // atlas is a dark picture and a white veil reads as fog, where a tint
                  // reads as a selection. Screening also stacks sensibly where two faces
                  // overlap — the region gets brighter, not muddier.
                  className="pointer-events-none absolute inset-0 h-full w-full opacity-60 mix-blend-screen"
                  style={{ filter: `sepia(1) saturate(6) hue-rotate(${o.rotate}deg)` }}
                />
              ))}
              {p.marker && (
                <>
                  <span className="pointer-events-none absolute inset-x-0 h-px bg-blue-400/70"
                    style={{ top: `${p.marker.y * 100}%` }} />
                  <span className="pointer-events-none absolute inset-y-0 w-px bg-blue-400/70"
                    style={{ left: `${p.marker.x * 100}%` }} />
                  <span
                    className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-blue-300 bg-blue-400/40"
                    style={{ left: `${p.marker.x * 100}%`, top: `${p.marker.y * 100}%` }}
                  />
                </>
              )}
              {p.waiting && (
                // THE SPINNER ALONE, over an undimmed picture. The scrim was hiding the
                // very thing being compared: the answer on screen while a new one is worked
                // out is the LAST correction, and holding it against the panel beside it is
                // most of what this second is for. A mark in the corner says "there is
                // another coming" without taking away what there is to look at.
                //
                // Its own contrast, since it no longer has a scrim to sit on: dark disc,
                // light spinner, legible over wood at either end of the range.
                <span className="pointer-events-none absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/70 shadow-lg">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </span>
              )}
              {p.boxes.map((b, bi) =>
                floating?.from.panel === i && floating.from.index === bi ? null : (
                  <div
                    key={bi}
                    className={`pointer-events-none absolute border-2 border-red-500 bg-red-500/10 ${
                      sel?.panel === i && sel.index === bi ? 'shadow-[0_0_0_2px_white]' : ''
                    }`}
                    style={boxStyle(b)}
                  >
                    {/* NUMBERED ONCE THERE ARE SEVERAL. A control underneath that speaks of
                        "2" needs a 2 to point at, and with one zone there is nothing to
                        tell apart and the badge is clutter on the picture. */}
                    {p.boxes.length > 1 && (
                      <span className="absolute -left-px -top-px bg-red-500 px-1 text-[10px] font-bold leading-tight text-white">
                        {bi + 1}
                      </span>
                    )}
                    {/* THE CORNERS, DRAWN. A rectangle you can resize and a rectangle you
                        cannot look identical, so the handles are the whole of how anybody
                        finds out — and getting the extent right against a live preview is
                        most of the work here, where deleting and redrawing is the same
                        gesture done twice.

                        `pointer-events-auto` for the CURSOR only. The press is caught by
                        the panel underneath and resolved by coordinate, so these do not
                        have to be hit precisely: anywhere within 14px of a corner starts
                        the resize, which is a target a hand can actually land on. */}
                    {/* THE WAY TO DROP THIS ONE, on the zone that is selected. Delete has
                        always removed it and always will, and a keystroke nobody is told
                        about is a keystroke nobody uses — so the gesture is on the picture,
                        where the thing it removes is.

                        ABOVE THE BOX, clear of all four corners: inside the top-right it
                        would sit in the 14px a hand aims at to resize from there, and the
                        two would fight over every press. Flipped inside for a zone drawn at
                        the very top of the picture, where outside is off the edge of it. */}
                    {canDraw && sel?.panel === i && sel.index === bi && (
                      <button
                        type="button"
                        title={t('notice.step3.regenDropBox')}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          p.onBoxes(p.boxes.filter((_, k) => k !== bi));
                          setSel(null);
                        }}
                        className={`pointer-events-auto absolute left-1/2 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow ring-1 ring-white transition-colors hover:bg-red-400 ${
                          Math.min(b[1], b[3]) < 0.06 ? 'top-1' : '-top-6'
                        }`}
                      >
                        <X className="h-3 w-3" strokeWidth={3} />
                      </button>
                    )}
                    {canDraw && [
                      ['-left-1 -top-1 cursor-nwse-resize'],
                      ['-right-1 -top-1 cursor-nesw-resize'],
                      ['-left-1 -bottom-1 cursor-nesw-resize'],
                      ['-right-1 -bottom-1 cursor-nwse-resize'],
                    ].map(([at]) => (
                      <span
                        key={at}
                        className={`pointer-events-auto absolute h-2 w-2 border border-white bg-red-500 ${at}`}
                      />
                    ))}
                  </div>
                ),
              )}
              {floating?.panel === i && (
                <div className="pointer-events-none absolute border-2 border-red-500 bg-red-500/10" style={boxStyle(floating.box)} />
              )}
              {draft?.panel === i && (
                <div className="pointer-events-none absolute border-2 border-red-500 bg-red-500/10" style={boxStyle(draft.box)} />
              )}
            </div>
            {p.below}
          </figure>
          );
        })}
        {/* Centred against the pictures, like the aside: it is a companion to the row, not
            a header over it.

            AND ITS SPACE IS HELD WHETHER OR NOT IT HAS ANYTHING IN IT. The column appears
            when the first face is picked, and a column appearing to the right of a centred
            row pushes every picture left — so the atlas you were pointing at slides out from
            under the cursor at the exact moment you were reading it. The width is fixed
            here, once, rather than left to whatever the list happens to contain. */}
        {side !== undefined && <div className="w-56 shrink-0 self-center">{side}</div>}
        </div>
        {footer && <div className="w-full">{footer}</div>}
      </div>
    </div>
  );
}
